import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, CreditCard, Loader2, Sparkles, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { PLANS, type Plan } from "@/lib/plans";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/_app/billing/plans")({
  component: AppPricingPage,
  head: () => ({
    meta: [
      { title: "Choose a plan · AdKavi" },
      { name: "description", content: "Pick a plan to start your 14-day free trial." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Audience = "creator" | "agency";

function AppPricingPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { plan: currentPlan, isActive } = useSubscription();
  const [selected, setSelected] = useState<Plan | null>(null);
  const [audience, setAudience] = useState<Audience>("creator");

  // Default audience based on profile.account_type (or current plan).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("account_type")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const acType = data?.account_type as Audience | null | undefined;
      if (acType === "agency" || acType === "creator") setAudience(acType);
      else if (currentPlan?.category) setAudience(currentPlan.category);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, currentPlan?.category]);

  const visible = PLANS.filter((p) => p.category === audience);

  const returnUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`
      : undefined;

  return (
    <section className="max-w-7xl mx-auto px-6 py-10">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center justify-center gap-3">
          <CreditCard className="w-8 h-8 text-orange-400" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">
            {isActive ? t("billing.title.switch") : t("billing.title.choose")}
          </span>
        </h1>
        <p className="mt-2 text-muted-foreground">
          {isActive && currentPlan
            ? t("billing.desc.switch", { plan: currentPlan.name })
            : t("billing.desc.trial")}
        </p>
      </div>

      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center rounded-full border border-border bg-card p-1">
          <ToggleBtn active={audience === "creator"} onClick={() => setAudience("creator")}>
            {t("billing.audience.creator")}
          </ToggleBtn>
          <ToggleBtn active={audience === "agency"} onClick={() => setAudience("agency")}>
            {t("billing.audience.agency")}
          </ToggleBtn>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {visible.map((p) => (
          <Card
            key={p.priceId}
            plan={p}
            isCurrent={currentPlan?.priceId === p.priceId && isActive}
            onSelect={setSelected}
          />
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("billing.dialog.title", { name: selected?.name ?? "" })}</DialogTitle>
            <DialogDescription>
              {t("billing.dialog.desc")}
            </DialogDescription>
          </DialogHeader>
          {selected && user && (
            <StripeEmbeddedCheckout
              priceId={selected.priceId}
              customerEmail={user.email ?? undefined}
              userId={user.id}
              returnUrl={returnUrl}
            />
          )}
          {selected && !user && (
            <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2 rounded-full text-sm font-medium transition ${
        active
          ? "bg-orange-500 text-white shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function Card({
  plan,
  isCurrent,
  onSelect,
}: {
  plan: Plan;
  isCurrent: boolean;
  onSelect: (p: Plan) => void;
}) {
  const { t } = useI18n();
  const popular = plan.popular;
  return (
    <div
      className={`relative p-6 rounded-3xl border flex flex-col ${
        popular
          ? "bg-gradient-to-br from-primary/15 to-secondary/10 border-primary"
          : "bg-card border-border"
      } ${isCurrent ? "ring-2 ring-primary/60" : ""}`}
    >
      {popular && !isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          <Star className="w-3 h-3" fill="currentColor" /> {t("billing.badge.popular")}
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          <Sparkles className="w-3 h-3" /> {t("billing.badge.current")}
        </div>
      )}
      <h3 className="font-bold text-lg mb-1">{plan.name}</h3>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-4xl font-bold">${plan.price.toLocaleString()}</span>
        <span className="text-sm text-muted-foreground">{t("billing.card.perMonth")}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-5">{t("billing.card.trial")}</p>
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2 text-sm">
            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">{f}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <Button variant="outline" disabled className="rounded-full">
          {t("billing.card.current")}
        </Button>
      ) : (
        <Button
          onClick={() => onSelect(plan)}
          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white"
        >
          {t("billing.card.choose", { name: plan.name })}
        </Button>
      )}
      {isCurrent && (
        <Link to="/settings" className="text-xs text-muted-foreground hover:text-foreground text-center mt-3">
          Manage billing →
        </Link>
      )}
    </div>
  );
}
